import { takeLatest, takeEvery, call, put, select } from 'redux-saga/effects'
import { replace } from 'connected-react-router'
import { SubmissionError } from 'redux-form'
import addMinutes from 'date-fns/addMinutes'
import { interviewRequestEntities } from '@/core/reducks'
import * as sessionView from '@/core/reducks/sessions'
import { errorHandler } from '@/core/helpers/errorHelpers'
import { showSystemError, hideSystemError } from '@/core/components/SystemErrorRedux'

import { fetchCandidateAvailabiltyEntitiesSaga } from '@/core/sagas/candidateAvailabilities'
import {
  fetchCandidateNoteEntitiesSaga,
  createCandidateNoteEntitySaga,
  updateCandidateNoteEntitySaga,
  deleteCandidateNoteEntitySaga,
} from '@/core/sagas/candidateNotes'
import { fetchGreenhouseProfileEntitySaga } from '@/core/sagas/greenhouseProfiles'
import { fetchInterviewRequestEntitySaga, updateInterviewRequestEntitySaga } from '@/core/sagas/hiringTeamInterviewRequests'
import { fetchHiringTeamMemberProfileEntitiesSaga } from '@/core/sagas/hiringTeamMemberProfiles'
import { fetchInterviewRequestCompetitionEntitiesSaga } from '@/core/sagas/interviewRequests'
import {
  fetchInterviewRequestInterviewEventEntitiesSaga,
  createInterviewRequestInterviewEventEntitySaga,
} from '@/core/sagas/interviewRequestEvents'
import {
  fetchInterviewRequestEventFeedbackEntitiesSaga,
  createInterviewRequestEventFeedbackEntitySaga,
} from '@/core/sagas/interviewRequestEventFeedbacks'
import {
  fetchInterviewRequestMessageEntitiesSaga,
  createInterviewRequestMessageEntitySaga,
  updateInterviewRequestMessageEntitySaga,
} from '@/core/sagas/interviewRequestMessages'
import { fetchInterviewRequestOfferEntitiesSaga, createInterviewRequestOfferEntitySaga } from '@/core/sagas/interviewRequestOffers'
import { fetchAccountManagerEntitiesSaga } from '@/core/sagas/accountManagers'

import { parseDate } from '@/core/helpers/dateTimeHelper'
import { hiringTeamId } from '@/core/selectors/currentUser'
import { greenhouseEnabled, companyId } from '@/core/selectors/sessions'
import { isInterviewRequestStale, interviewRequestCandidate, pastInterviewEvents } from '../selectors/interviewRequestDetails'
import { interviewRequestDetailsView } from '../reducks'

export function* fetchInterviewRequestSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(fetchInterviewRequestEntitySaga, hiringTeam, meta.id)

    // If IR is withdrawn, show 404 page
    if (response.result.status === 'withdrawn') {
      throw Error('Message not found')
    }

    yield put(interviewRequestDetailsView.fetchInterviewRequestSuccess(meta.id, response))

    // If the company has integration to greenhouse, trigger fetch
    const syncingGreenhouse = yield select(greenhouseEnabled)
    if (syncingGreenhouse) {
      yield put(interviewRequestDetailsView.fetchGreenhouseProfile(meta.id))
    }

    const stale = yield select(isInterviewRequestStale(), response.result.id)
    if (!stale) {
      yield put(interviewRequestDetailsView.fetchCandidateAvailabilities(meta.id, response.result.candidate_profile.id))
      yield put(interviewRequestDetailsView.fetchCompetingInterviewRequests(meta.id))
    }

    // Initialise polling
    const candidate = yield select(interviewRequestCandidate(), meta.id)
    yield put(interviewRequestDetailsView.interviewRequestPollingInit(meta.id, meta.companySlug, candidate.id))
  } catch (err) {
    const { error, status } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchInterviewRequestError(meta.id, error))

    if (status !== 404 && status !== -1) {
      yield put(showSystemError(error))
    }
  }
}

export function* fetchGreenhouseProfileSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(fetchGreenhouseProfileEntitySaga, meta.id)
    yield put(interviewRequestDetailsView.fetchGreenhouseProfileSuccess(meta.id, response))
  } catch (err) {
    const { error, status } = yield call(errorHandler, err)

    if (status === 404) {
      yield put(interviewRequestDetailsView.fetchGreenhouseProfileSuccess(meta.id, { result: {} }))
      return
    }

    yield put(interviewRequestDetailsView.fetchGreenhouseProfileError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* fetchCompetingInterviewRequestsSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(fetchInterviewRequestCompetitionEntitiesSaga, meta.id)
    yield put(interviewRequestDetailsView.fetchCompetingInterviewRequestsSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchCompetingInterviewRequestsError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* fetchCandidateAvailabilitiesSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(fetchCandidateAvailabiltyEntitiesSaga, meta.candidateProfileId)
    yield put(interviewRequestDetailsView.fetchCandidateAvailabilitiesSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchCandidateAvailabilitiesError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* fetchMessagesSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(fetchInterviewRequestMessageEntitiesSaga, meta.id, meta.params)
    yield put(interviewRequestDetailsView.fetchMessagesSuccess(meta.id, response))

    // Trigger a re-fetch of the session metadata as the count could have changed
    yield put(sessionView.fetchMetadata())
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchMessagesError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* createMessageSaga({ meta }) {
  try {
    // Transform the data to the structure expected by the server
    const { text, file } = meta.data
    const response = yield call(createInterviewRequestMessageEntitySaga, meta.id, {
      text,
      ...(file && { file_name: file.name, file_url: file.url }),
    })
    yield put(interviewRequestDetailsView.createMessageSuccess(meta.id, response))
    yield call(meta.formResolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't send your message.")
    yield put(interviewRequestDetailsView.createMessageError(meta.id, error))
    // Attach the error to the message field so it looks nice
    yield call(meta.formReject, new SubmissionError({ text: error }))
  }
}

export function* updateMessageSaga({ meta }) {
  try {
    yield call(updateInterviewRequestMessageEntitySaga, meta.id, meta.messageId, { text: meta.data.text })
    yield put(interviewRequestDetailsView.updateMessageSuccess(meta.id))
    yield call(meta.formResolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't update your message.")
    // handleError swaps the body of a 403 or 422 for generic copy, which would tell a member
    // whose edit window has closed that their session expired. Prefer what the endpoint said.
    const message = err.response?.data?.error || error

    yield put(interviewRequestDetailsView.updateMessageError(meta.id, message))
    yield call(meta.formReject, new SubmissionError({ _error: message }))
  }
}

export function* withdrawInterviewRequestSaga({ meta }) {
  try {
    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateInterviewRequestEntitySaga, hiringTeam, meta.id, { status: 'withdrawn' })
    yield call(meta.formResolve)
    yield put(replace(`/company/${meta.companySlug}/interview_requests`))

    // Execute success last to avoid issues with re-renders
    yield put(interviewRequestDetailsView.withdrawInterviewRequestSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't withdraw the request.")
    yield put(interviewRequestDetailsView.withdrawInterviewRequestError(meta.id, error))
    yield call(meta.formReject, new SubmissionError({ _error: error }))
  }
}

export function* archiveInterviewRequestSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateInterviewRequestEntitySaga, hiringTeam, meta.id, meta.data)
    yield put(interviewRequestDetailsView.archiveInterviewRequestSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.archiveInterviewRequestError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* updateInterviewRequestSaga({ meta }) {
  try {
    yield put(hideSystemError())

    // Fake the update first so users don't see delay
    yield put(interviewRequestEntities.updateItem(meta.id, meta.data))

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateInterviewRequestEntitySaga, hiringTeam, meta.id, meta.data)
    yield put(interviewRequestDetailsView.updateInterviewRequestSuccess(meta.id, response))
  } catch (err) {
    // Rollback the changes we faked
    yield put(interviewRequestEntities.updateItem(meta.id, meta.previousData))

    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.updateInterviewRequestError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* fetchInterviewEventsSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(
      fetchInterviewRequestInterviewEventEntitiesSaga,
      meta.id,
      meta.sortBy,
      meta.sortDirection,
      meta.interviewSource,
    )
    yield put(interviewRequestDetailsView.fetchInterviewEventsSuccess(meta.id, response))
    yield put(interviewRequestDetailsView.fetchEventFeedbacks(meta.id))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchInterviewEventsError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* fetchEventFeedbacksSaga({ meta }) {
  try {
    yield put(hideSystemError())
    const pastEvents = yield select(pastInterviewEvents(), meta.id)

    if (pastEvents.length > 0) {
      const response = yield call(fetchInterviewRequestEventFeedbackEntitiesSaga, meta.id, {
        interview_event_ids: pastEvents.map(event => event.id),
      })
      yield put(interviewRequestDetailsView.fetchEventFeedbacksSuccess(meta.id, response))
    } else {
      // Fake a successful fetch with empty array to avoid a "stuck" fetching flag
      yield put(interviewRequestDetailsView.fetchEventFeedbacksSuccess(meta.id, { result: [] }))
    }
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchEventFeedbacksError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* createInterviewEventSaga({ meta }) {
  try {
    // Spread data and delete the invalid fields to preserve the dynamic values

    const data = {
      ...meta.data,
      calendar: undefined,
      datetime: undefined,
      duration: typeof meta.data.duration === 'number' ? meta.data.duration.toString() : undefined,
      category: undefined,
      description: meta.description || meta.data.description,
      interviewRequestId: meta.id,
      interviewType: meta.data.category || meta.data.interviewType,
    }
    if (meta.data.datetime) {
      data.startAt = meta.data.datetime
      data.endAt = addMinutes(parseDate(meta.data.datetime), meta.data.duration)
    }

    const response = yield call(createInterviewRequestInterviewEventEntitySaga, data)

    yield put(interviewRequestDetailsView.createInterviewEventSuccess(meta.id, response))
    if (meta.formResolve) yield call(meta.formResolve)

    // Re-fetch candidate availabilities as these change with the creation of an interview
    yield put(interviewRequestDetailsView.fetchCandidateAvailabilities(meta.id, meta.candidateId))
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't schedule the interview.")
    yield put(interviewRequestDetailsView.createInterviewEventError(meta.id, error))
    if (meta.formReject) {
      yield call(meta.formReject, new SubmissionError({ _error: error }))
    }
  }
}

export function* createEventFeedbackSaga({ meta }) {
  try {
    const response = yield call(createInterviewRequestEventFeedbackEntitySaga, meta.id, { interview_event_id: meta.eventId, ...meta.data })
    yield put(interviewRequestDetailsView.createEventFeedbackSuccess(meta.id, meta.eventId, response))
    yield call(meta.formResolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't send the feedback.")
    yield put(interviewRequestDetailsView.createEventFeedbackError(meta.id, meta.eventId, error))
    yield call(meta.formReject)
  }
}

export function* fetchOffersSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(fetchInterviewRequestOfferEntitiesSaga, meta.id)
    yield put(interviewRequestDetailsView.fetchOffersSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchOffersError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* createOfferSaga({ meta }) {
  try {
    const data = {
      text: meta.data.text,
      file_name: meta.data.file.name,
      file_url: meta.data.file.url,
    }
    const response = yield call(createInterviewRequestOfferEntitySaga, meta.id, data)
    yield put(interviewRequestDetailsView.createOfferSuccess(meta.id, response))
    yield call(meta.formResolve)

    // Trigger an update of the IR as several properties might have changed (ats_status, archived, updated_at, and last_message)
    const hiringTeam = yield select(hiringTeamId)
    const updateResponse = yield call(fetchInterviewRequestEntitySaga, hiringTeam, meta.id)
    yield put(interviewRequestDetailsView.updateInterviewRequestSuccess(meta.id, updateResponse))
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't extend the offer.")
    yield put(interviewRequestDetailsView.createOfferError(meta.id, error))
    yield call(meta.formReject, new SubmissionError({ _error: error }))
  }
}

export function* fetchMembersSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(fetchHiringTeamMemberProfileEntitiesSaga, hiringTeam, meta.params)
    yield put(interviewRequestDetailsView.fetchMembersSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchMembersError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* fetchTeamNotesSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(fetchCandidateNoteEntitiesSaga, hiringTeam, meta.params)
    yield put(interviewRequestDetailsView.fetchTeamNotesSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchTeamNotesError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* createTeamNoteSaga({ meta }) {
  try {
    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(createCandidateNoteEntitySaga, hiringTeam, meta.data)
    yield put(interviewRequestDetailsView.createTeamNoteSuccess(meta.id, response))
    yield call(meta.formResolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't post your team note.")
    yield put(interviewRequestDetailsView.createTeamNoteError(meta.id, error))
    yield call(meta.formReject, new SubmissionError({ _error: error }))
  }
}

export function* updateTeamNoteSaga({ meta }) {
  try {
    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateCandidateNoteEntitySaga, hiringTeam, meta.teamNoteId, meta.data)
    yield put(interviewRequestDetailsView.updateTeamNoteSuccess(meta.id, response))
    yield call(meta.formResolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't update your team note.")
    yield put(interviewRequestDetailsView.updateTeamNoteError(meta.id, error))
    yield call(meta.formReject, new SubmissionError({ _error: error }))
  }
}

export function* deleteTeamNoteSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    yield call(deleteCandidateNoteEntitySaga, hiringTeam, meta.teamNoteId)
    yield put(interviewRequestDetailsView.deleteTeamNoteSuccess(meta.id))
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't delete your team note.")
    yield put(interviewRequestDetailsView.deleteTeamNoteError(meta.id, error))

    yield put(showSystemError(error))
  }
}

export function* fetchAccountManagerSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const id = yield select(companyId)

    const response = yield call(fetchAccountManagerEntitiesSaga, id)
    yield put(interviewRequestDetailsView.fetchAccountManagerSuccess(meta.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestDetailsView.fetchAccountManagerError(meta.id, error))
    yield put(showSystemError(error))
  }
}

export function* interviewRequestPollingExecuteSaga({ meta }) {
  try {
    yield put(hideSystemError())

    // Re-fetch the IR details
    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(fetchInterviewRequestEntitySaga, hiringTeam, meta.id)
    yield put(interviewRequestDetailsView.fetchInterviewRequestSuccess(meta.id, response))

    // If IR is withdrawn on poll, redirect to list
    if (response.result.status === 'withdrawn') {
      yield put(replace(`/company/${meta.companySlug}/interview_requests`))
      // Call success action to purge the redux store
      yield put(interviewRequestDetailsView.withdrawInterviewRequestSuccess(meta.id, response))
      return
    }

    // Re-fetch the messages
    yield put(interviewRequestDetailsView.fetchMessages(meta.id))

    // Re-fetch the interview events
    yield put(interviewRequestDetailsView.fetchInterviewEvents(meta.id))

    // Re-fetch the team notes
    yield put(interviewRequestDetailsView.fetchTeamNotes(meta.id, meta.candidateId))

    // Re-fetch the offers
    yield put(interviewRequestDetailsView.fetchOffers(meta.id))

    // Re-fetch candidate availabilities
    const stale = yield select(isInterviewRequestStale(), meta.id)
    if (!stale) {
      yield put(interviewRequestDetailsView.fetchCandidateAvailabilities(meta.id, meta.candidateId))
    }
  } catch (err) {
    const { error } = yield call(errorHandler, err, 'Something went wrong. Please try again.')
    yield put(showSystemError(error))
  }
}

export default function* watchInterviewRequestDetailsSaga() {
  yield takeLatest(interviewRequestDetailsView.FETCH_INTERVIEW_REQUEST, fetchInterviewRequestSaga)
  yield takeLatest(interviewRequestDetailsView.FETCH_GREENHOUSE_PROFILE, fetchGreenhouseProfileSaga)
  yield takeLatest(interviewRequestDetailsView.FETCH_COMPETING_INTERVIEW_REQUESTS, fetchCompetingInterviewRequestsSaga)

  yield takeLatest(interviewRequestDetailsView.FETCH_CANDIDATE_AVAILABILITIES, fetchCandidateAvailabilitiesSaga)

  yield takeLatest(interviewRequestDetailsView.FETCH_MESSAGES, fetchMessagesSaga)
  yield takeLatest(interviewRequestDetailsView.CREATE_MESSAGE, createMessageSaga)
  yield takeLatest(interviewRequestDetailsView.UPDATE_MESSAGE, updateMessageSaga)

  yield takeLatest(interviewRequestDetailsView.WITHDRAW_INTERVIEW_REQUEST, withdrawInterviewRequestSaga)
  yield takeLatest(interviewRequestDetailsView.ARCHIVE_INTERVIEW_REQUEST, archiveInterviewRequestSaga)
  yield takeEvery(interviewRequestDetailsView.UPDATE_INTERVIEW_REQUEST, updateInterviewRequestSaga)

  yield takeLatest(interviewRequestDetailsView.FETCH_INTERVIEW_EVENTS, fetchInterviewEventsSaga)
  yield takeLatest(interviewRequestDetailsView.CREATE_INTERVIEW_EVENT, createInterviewEventSaga)
  yield takeLatest(interviewRequestDetailsView.FETCH_EVENT_FEEDBACKS, fetchEventFeedbacksSaga)
  yield takeLatest(interviewRequestDetailsView.CREATE_EVENT_FEEDBACK, createEventFeedbackSaga)

  yield takeLatest(interviewRequestDetailsView.FETCH_OFFERS, fetchOffersSaga)
  yield takeLatest(interviewRequestDetailsView.CREATE_OFFER, createOfferSaga)

  yield takeLatest(interviewRequestDetailsView.FETCH_MEMBERS, fetchMembersSaga)
  yield takeLatest(interviewRequestDetailsView.FETCH_TEAM_NOTES, fetchTeamNotesSaga)
  yield takeLatest(interviewRequestDetailsView.CREATE_TEAM_NOTE, createTeamNoteSaga)
  yield takeLatest(interviewRequestDetailsView.UPDATE_TEAM_NOTE, updateTeamNoteSaga)
  yield takeLatest(interviewRequestDetailsView.DELETE_TEAM_NOTE, deleteTeamNoteSaga)

  yield takeLatest(interviewRequestDetailsView.FETCH_ACCOUNT_MANAGER, fetchAccountManagerSaga)

  yield takeLatest(interviewRequestDetailsView.INTERVIEW_REQUEST_POLLING_EXECUTE, interviewRequestPollingExecuteSaga)
}
